import { useState, useEffect, useMemo, ComponentType } from 'react';
import { api } from '../services/api';
import { extractApiError } from '../utils/errorUtils';

/**
 * Feature Flag Hook for React Components
 *
 * Provides runtime feature toggling based on database-backed feature flags.
 * Supports multiple usage patterns for different use cases.
 *
 * @see CLAUDE.md "Database-Backed Feature Flag System" for complete documentation
 * @see IR.md step 9 for when to use feature flags
 */

interface FeatureFlagResult {
  isEnabled: boolean;
  isLoading: boolean;
  error: Error | null;
  flagName: string | null;
  value: string | null;  // Issue #1513: Support string values (e.g., 'calendar', 'listing', 'marketing')
}

/**
 * Pattern 1: Hook-based (Recommended for simple cases)
 *
 * @param flagKey - Feature flag key (e.g., 'new_dashboard')
 * @returns Object with isEnabled, isLoading, error, and flagName
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isEnabled, isLoading } = useFeatureFlag('new_dashboard');
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (isEnabled) return <NewDashboard />;
 *   return <OldDashboard />;
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFeatureFlag(flagKey: string): FeatureFlagResult {
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [flagName, setFlagName] = useState<string | null>(null);
  const [value, setValue] = useState<string | null>(null);  // Issue #1513

  useEffect(() => {
    let isMounted = true;

    const checkFlag = async () => {
      try {
        console.log(`[FeatureFlag] Checking flag: ${flagKey}`);
        setIsLoading(true);
        setError(null);

        const response = await api.get(`/feature-flags/${flagKey}`);
        console.log(`[FeatureFlag] Response for ${flagKey}:`, response.data);

        if (isMounted) {
          const enabled = response.data.is_enabled || false;
          const flagValue = response.data.flag_value || null;
          setIsEnabled(enabled);
          setFlagName(response.data.flag_name || null);
          setValue(flagValue);  // Issue #1513
          console.log(`[FeatureFlag] ${flagKey} enabled:`, enabled, 'value:', flagValue);
        }
      } catch (err: unknown) {
        if (isMounted) {
          // Safe-by-default: Default to disabled on error
          const apiError = extractApiError(err);
          setIsEnabled(false);
          setError(new Error(apiError.message));
          console.error(`[FeatureFlag] Check failed for ${flagKey}:`, apiError);
          console.error(`[FeatureFlag] Full error:`, err);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkFlag();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [flagKey]);

  return { isEnabled, isLoading, error, flagName, value };
}

/**
 * Pattern 2: Wrapper Component (Recommended for conditional rendering)
 *
 * @param props.flagKey - Feature flag key
 * @param props.children - Content to show when flag is enabled
 * @param props.fallback - Content to show when flag is disabled (optional)
 * @param props.inverse - If true, shows children when flag is DISABLED (default: false)
 * @param props.loadingFallback - Content to show while loading (optional)
 *
 * @example
 * ```tsx
 * <FeatureFlag flagKey="new_dashboard" fallback={<OldDashboard />}>
 *   <NewDashboard />
 * </FeatureFlag>
 *
 * // Inverse flag (show when DISABLED)
 * <FeatureFlag flagKey="new_feature" inverse={true} fallback={null}>
 *   <DeprecationWarning message="This feature will be removed soon" />
 * </FeatureFlag>
 * ```
 */
interface FeatureFlagProps {
  flagKey: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  inverse?: boolean;
  loadingFallback?: React.ReactNode;
}

export function FeatureFlag({
  flagKey,
  children,
  fallback = null,
  inverse = false,
  loadingFallback = null
}: FeatureFlagProps) {
  const { isEnabled, isLoading } = useFeatureFlag(flagKey);

  if (isLoading) {
    return <>{loadingFallback}</>;
  }

  // Inverse mode: show children when flag is DISABLED
  if (inverse) {
    return <>{isEnabled ? fallback : children}</>;
  }

  // Normal mode: show children when flag is ENABLED
  return <>{isEnabled ? children : fallback}</>;
}

/**
 * Pattern 3: Higher-Order Component (Recommended for route-level protection)
 *
 * @param flagKey - Feature flag key
 * @param options.fallback - Component to render when flag is disabled
 * @param options.loadingFallback - Component to render while loading
 * @returns HOC that wraps the component with feature flag check
 *
 * @example
 * ```tsx
 * const FlaggedDashboard = withFeatureFlag('new_dashboard', {
 *   fallback: OldDashboard
 * })(NewDashboard);
 *
 * // Use in routes
 * <Route path="/dashboard" element={<FlaggedDashboard />} />
 * ```
 */
interface WithFeatureFlagOptions {
  fallback?: ComponentType;
  loadingFallback?: ComponentType;
}

// eslint-disable-next-line react-refresh/only-export-components
export function withFeatureFlag(
  flagKey: string,
  options: WithFeatureFlagOptions = {}
) {
  return function <P extends object>(Component: ComponentType<P>) {
    return function FeatureFlaggedComponent(props: P) {
      const { isEnabled, isLoading } = useFeatureFlag(flagKey);
      const { fallback: FallbackComponent, loadingFallback: LoadingComponent } = options;

      if (isLoading && LoadingComponent) {
        return <LoadingComponent />;
      }

      if (!isEnabled && FallbackComponent) {
        return <FallbackComponent />;
      }

      if (!isEnabled) {
        return null;
      }

      return <Component {...props} />;
    };
  };
}

/**
 * Pattern 4: Multiple Flags Check
 *
 * @param flagKeys - Array of feature flag keys
 * @param mode - 'any' (OR logic) or 'all' (AND logic)
 * @returns Object with isEnabled (based on mode), isLoading, and individual flag states
 *
 * @example
 * ```tsx
 * function AdvancedFeature() {
 *   const { isEnabled, isLoading } = useMultipleFeatureFlags(
 *     ['feature_a', 'feature_b'],
 *     'all' // Both must be enabled
 *   );
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (!isEnabled) return <FeatureDisabled />;
 *   return <AdvancedComponent />;
 * }
 * ```
 */
interface MultipleFeatureFlagsResult {
  isEnabled: boolean;
  isLoading: boolean;
  flags: Record<string, boolean>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMultipleFeatureFlags(
  flagKeys: string[],
  mode: 'any' | 'all' = 'all'
): MultipleFeatureFlagsResult {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Serialize flagKeys for dependency comparison and create stable array reference
  const flagKeysString = flagKeys.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const flagKeysArray = useMemo(() => [...flagKeys], [flagKeysString]); // flagKeysString provides value-based comparison instead of reference equality

  useEffect(() => {
    let isMounted = true;

    const checkFlags = async () => {
      try {
        setIsLoading(true);

        const results = await Promise.all(
          flagKeysArray.map(async (key) => {
            try {
              const response = await api.get(`/feature-flags/${key}`);
              return { key, enabled: response.data.is_enabled || false };
            } catch {
              // Safe-by-default: Default to disabled on error
              return { key, enabled: false };
            }
          })
        );

        if (isMounted) {
          const flagState = results.reduce((acc, { key, enabled }) => {
            acc[key] = enabled;
            return acc;
          }, {} as Record<string, boolean>);

          setFlags(flagState);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkFlags();

    return () => {
      isMounted = false;
    };
  }, [flagKeysArray]); // mode is used for calculating isEnabled but doesn't affect the API calls, so it's not needed in dependencies

  const isEnabled = mode === 'all'
    ? flagKeys.every(key => flags[key])
    : flagKeys.some(key => flags[key]);

  return { isEnabled, isLoading, flags };
}
